<template>
  <section id="district-applications-container">
    <ThePublicFilters
      :is-fetching-district-apps="isFetchingDistrictApps"
      @update:table-filters="onTableFiltersUpdate"
    />
    
    <DataTable
      data-key="id"
      lazy
      :loading="isFetchingDistrictApps"
      paginator
      removable-sort
      :rows="pagination.PageSize"
      :rows-per-page-options="[10, 25, 50, 100]"
      size="large"
      :total-records="totalNoOfRecords"
      :value="districtApplications"
      @page="onPageChange"
      @sort="onSortChange"
    >
      <template #empty>
        <span v-if="!isFetchingDistrictApps && !districtApplications.length">
          No applications have been used in the district in last 12 months.
        </span>
      </template>
      <Column
        field="appName"
        header="Applications used during last 12 months"
        :sortable="true"
      >
        <template #body="{ data }">
          <ApplicationName
            :domain="data.domainName"
            :logo="data.logo"
            :name="data.appName"
          />
        </template>
      </Column>
      <Column
        field="approvalStatus"
        header="Approval status"
        style="width: 25%;"
      >
        <template #body="{ data }">
          <ReviewStatus :review-status="data.reviewStatus" />
        </template>
      </Column>
    </DataTable>

    <TheApiEnvironmentSelect v-if="shouldShowEnvironmentSelect" />
    <Toast />
  </section>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { DataTable } from "primevue";
import Column from "primevue/column";
import type { DataTablePageEvent, DataTableSortEvent } from "primevue/datatable";
import Toast from "primevue/toast";
import { computed } from "vue";

import ApplicationName from "../../../common/ApplicationName.vue";
import ReviewStatus from "../common/columns/ReviewStatus.vue";
import TheApiEnvironmentSelect from "@/components/internal-configuration/components/TheApiEnvironmentSelect.vue";
import ThePublicFilters from "./ThePublicFilters.vue";

import { usePublicDistrictApplications } from "@/composables/api/resources/usePublicDistrictApplications.ts";
import { useTableFilters } from "@/composables/district-applications-list/public/useTableFilters.ts";
import { useTableOrdering } from "@/composables/shared/useTableOrdering.ts";
import { useTablePagination } from "@/composables/shared/useTablePagination.ts";

import { useEnvironmentStore } from "@/stores/useEnvironmentStore.ts";

import type {
    PublicDistrictApplicationsOrderByField,
} from "@/composables/api/repositories/pgc/useDistrictsRepository.ts";

const { availableEnvironments } = storeToRefs(useEnvironmentStore());
const shouldShowEnvironmentSelect = computed(() => {
    return window.self === window.top && availableEnvironments.value.findIndex((environment) => environment.code === "local") !== -1;
});

const districtToken = computed(() => {
    return new URLSearchParams(window.location.search).get("token");
});

const { filters, onTableFiltersUpdate } = useTableFilters();

const { pagination, setPage, setPageSize } = useTablePagination();
function onPageChange(event: DataTablePageEvent) {
    setPage(event.page + 1);
    setPageSize(event.rows);
}

const { ordering, setOrderBy, setOrderDirection } = useTableOrdering<PublicDistrictApplicationsOrderByField>("appName", "asc");

function onSortChange(event: DataTableSortEvent) {
    setPage(1);
    setPageSize(event.rows);

    setOrderBy(event.sortField as PublicDistrictApplicationsOrderByField);
    setOrderDirection(event.sortOrder);
}

const {
    districtApplications,
    isFetching: isFetchingDistrictApps,
    totalNoOfRecords,
    fetchPublicDistrictApplications,
} = usePublicDistrictApplications({ pagination, ordering, filters, districtToken });
void fetchPublicDistrictApplications();
</script>

<style scoped>
#district-applications-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
</style>